import { FolderOutlined, UploadOutlined } from "@ant-design/icons"
import { Button, Card, Flex, Typography, Upload } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { AttachmentsList } from "../../../../components/AttachmentsList"
import { validateFile } from "../../../../utils/file-validation.ts"
import { OverviewCardTitle } from "../../../Lead/LeadWorkspacePage/overview/OverviewCardIcon.tsx"
import type { Project, ProjectUpdateData } from "../../types/project.types.ts"
import {
  ALLOWED_FILE_EXTENSIONS,
  MAX_SIZE_MB
} from "./project-attachment-upload.ts"

const { Text } = Typography

interface ProjectFilesCardProps {
  project: Project
  canEdit?: boolean
  onSave: (changed: Partial<ProjectUpdateData>) => Promise<boolean>
}

// Upload/delete both PATCH through the exact same file_list /
// deleted_attachment_ids fields the old batched form submit used — just one
// action at a time instead of staged until the whole form's Save Changes.
function ProjectFilesCard({ project, canEdit, onSave }: ProjectFilesCardProps) {
  const { t } = useTranslation()

  const attachments = project?.attachments?.filter((f: any) => !f?.is_archived) ?? []

  // createFormData (utils/form-data/create-form-data.ts) reads
  // file_list[].file.originFileObj — antd's own UploadFile wrapper shape.
  // Reading that off onChange's info.file looks right but originFileObj is
  // not reliably populated there yet (it appends "undefined" to the
  // request — a real file field that silently carries no file, which the
  // backend accepts as a no-op, hence "success" with nothing to show for
  // it). beforeUpload's raw File is what's actually reliable, so the
  // wrapper is built here directly instead of trusting antd's own state.
  const handleUpload = (file: File) => {
    const result = validateFile(file, {
      maxSize: MAX_SIZE_MB,
      allowedExtensions: ALLOWED_FILE_EXTENSIONS
    })
    if (result === Upload.LIST_IGNORE) return false

    void onSave({
      file_list: [{ file_type: 5, file: { originFileObj: file } }]
    } as unknown as Partial<ProjectUpdateData>)
    return false
  }

  const handleDelete = (file: { id: string }) => {
    void onSave({ deleted_attachment_ids: [file.id] } as Partial<ProjectUpdateData>)
  }

  return (
    <Card size={"small"} className={"h-full"}>
      <Flex vertical gap={"small"} className={"w-full h-full"}>
        <Flex justify={"space-between"} align={"center"}>
          <OverviewCardTitle icon={<FolderOutlined />} accent={"orange"}>
            {t("projectWorkspace.cards.files.title")}
          </OverviewCardTitle>
          {canEdit && (
            <Upload
              multiple
              showUploadList={false}
              accept={ALLOWED_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
              beforeUpload={handleUpload}
            >
              <Button size={"small"} icon={<UploadOutlined />}>
                {t("projectWorkspace.cards.files.upload")}
              </Button>
            </Upload>
          )}
        </Flex>

        {attachments.length ? (
          <AttachmentsList
            attachments={attachments}
            preview
            onDelete={canEdit ? handleDelete : undefined}
          />
        ) : (
          <Flex
            vertical
            align={"center"}
            justify={"center"}
            gap={4}
            className={
              "flex-1 min-h-[140px] rounded-lg border border-dashed border-border p-3"
            }
          >
            <UploadOutlined className={"text-lg text-secondary"} />
            <Text type={"secondary"}>{t("projectWorkspace.cards.files.empty")}</Text>
            <Text type={"secondary"} className={"text-xs"}>
              {t("projectWorkspace.cards.files.emptyHint")}
            </Text>
          </Flex>
        )}
      </Flex>
    </Card>
  )
}

export { ProjectFilesCard }
